import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLang } from '../LanguageContext';

const T = {
  searchTitle:   { ko: '제목', en: 'Title' },
  searchContent: { ko: '내용', en: 'Content' },
  searchPh:      { ko: '검색어를 입력하세요', en: 'Search…' },
  searchBtn:     { ko: '검색', en: 'Search' },
  first:         { ko: '처음', en: 'First' },
  last:          { ko: '끝', en: 'Last' },
  count:         { ko: (a, b, c) => `${a}–${b} / 전체 ${c}건`,
                   en: (a, b, c) => `${a}–${b} of ${c}` },
  empty:         { ko: '등록된 글이 없습니다.', en: 'Nothing here yet.' },
  noResult:      { ko: '검색 결과가 없습니다.', en: 'No results.' },
  views:         { ko: '조회', en: 'Views' },
  attach:        { ko: '첨부파일', en: 'Attachment' },
  download:      { ko: '첨부파일 다운로드', en: 'Download' },
  back:          { ko: '목록', en: 'Back to list' },
  prev:          { ko: '이전', en: 'Previous' },
  next:          { ko: '다음', en: 'Next' },
};

const PAGE = 10;

// DB 제목 뒤의 "  [출처] 경기신문 (http...)" 꼬리는 화면에서 떼어낸다
export function cleanTitle(s) {
  if (!s) return '';
  return String(s).split('[출처]')[0].replace(/\s+/g, ' ').trim();
}
export function fmtDate(s) {
  return s ? String(s).slice(0, 10) : '';
}

// 본문의 맨 URL 을 링크로 바꾼다(HTML 본문이 아닌 경우에만 사용)
function Linkify({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p)
      ? <a key={i} href={p} target="_blank" rel="noopener noreferrer">{p}</a>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

function toEmbed(url) {
  const u = String(url || '').trim();
  const m = u.match(/(?:youtu\.be\/|watch\?v=|embed\/)([A-Za-z0-9_-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

/**
 * 게시판 — 목록 + 상세를 한 컴포넌트에서 처리한다.
 * category: 'NOTICE' | 'PRESS' (없으면 전체)
 * 목록은 항상 사이트 안에서 본문을 연다. 원문 링크는 본문 안에서 누른다.
 */
function Board({ category, tag }) {
  const { lang } = useLang();
  const bodyRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState(null);
  const [stype, setStype] = useState('title');
  const [sword, setSword] = useState('');
  const [query, setQuery] = useState('');

  const pick = (n, k) => (lang === 'en' && n[k + '_EN'] ? n[k + '_EN'] : n[k]);

  const load = useCallback(() => {
    const q = category ? `?category=${category}` : '';
    fetch('/api/notices' + q)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setRows(Array.isArray(d) ? d : []); setPage(1); })
      .catch(() => setRows([]));
  }, [category]);

  useEffect(load, [load]);

  // ?idx=123 으로 들어오면 그 글을 바로 펼친다
  useEffect(() => {
    const idx = new URLSearchParams(window.location.search).get('idx');
    if (!idx) return;
    fetch(`/api/notice/${idx}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setSel(d); })
      .catch(() => {});
  }, []);

  const open = (n) => {
    // 조회수 증가 + 전체 본문은 상세 API 로 받는다
    fetch(`/api/notice/${n.IDX}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setSel(d || n))
      .catch(() => setSel(n));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 본문 HTML 안의 링크는 관리자가 에디터로 넣은 것이라 target 이 없다.
  // 기사 원문으로 그냥 이동해 버리면 사이트를 벗어나므로 새 탭으로 바꿔준다.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.querySelectorAll('a[href^="http"]').forEach((a) => {
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    });
  });

  // ── 상세 ──────────────────────────────────────────
  if (sel) {
    const list = rows || [];
    const at = list.findIndex((n) => n.IDX === sel.IDX);
    const prev = at > 0 ? list[at - 1] : null;
    const next = at >= 0 && at < list.length - 1 ? list[at + 1] : null;
    const body = pick(sel, 'CONTENT') || '';
    const embed = toEmbed(sel.VIDEO_URL);

    return (
      <div className="sp-detail">
        <div className="sp-dhead">
          <h2>{cleanTitle(pick(sel, 'TITLE'))}</h2>
          <div className="sp-dmeta">
            <span>{sel.AUTHOR || 'medilogis'}</span>
            <span>{fmtDate(sel.CREATED_AT)}</span>
            <span>{T.views[lang]} {sel.VIEW_COUNT || 0}</span>
          </div>
          {sel.FILE_URL && (
            <div className="sp-dfile">
              <span className="lbl">{T.attach[lang]}</span>
              <a href={sel.FILE_URL} download={sel.FILE_NAME || true}>
                {sel.FILE_NAME || T.download[lang]}
              </a>
            </div>
          )}
        </div>
        <div className="sp-dbody" ref={bodyRef}>
          {body.includes('<')
            ? <div dangerouslySetInnerHTML={{ __html: body }} />
            : <p><Linkify text={body} /></p>}
          {embed && (
            <div className="sp-dvideo">
              <iframe src={embed} title="video" allowFullScreen />
            </div>
          )}
          {!embed && sel.VIDEO_URL && (
            <p><a href={String(sel.VIDEO_URL).trim()} target="_blank" rel="noopener noreferrer">
              {String(sel.VIDEO_URL).trim()}
            </a></p>
          )}
        </div>
        <div className="sp-dnav">
          <div className="side">
            {prev && (
              <button type="button" className="sp-dbtn" onClick={() => open(prev)}>
                ← {T.prev[lang]} <span className="t">{cleanTitle(pick(prev, 'TITLE'))}</span>
              </button>
            )}
            {next && (
              <button type="button" className="sp-dbtn" onClick={() => open(next)}>
                {T.next[lang]} <span className="t">{cleanTitle(pick(next, 'TITLE'))}</span> →
              </button>
            )}
          </div>
          <button type="button" className="sp-dbtn on" onClick={() => { setSel(null); load(); }}>
            {T.back[lang]}
          </button>
        </div>
      </div>
    );
  }

  // ── 목록 ──────────────────────────────────────────
  const all = rows || [];
  const q = query.trim().toLowerCase();
  const list = !q ? all : all.filter((n) => {
    const hay = stype === 'title' ? cleanTitle(pick(n, 'TITLE')) : (pick(n, 'CONTENT') || '');
    return String(hay).toLowerCase().includes(q);
  });

  const total = Math.max(1, Math.ceil(list.length / PAGE));
  const go = (n) => {
    setPage(Math.min(total, Math.max(1, n)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 페이지가 많아지면 현재 위치 주변만 보여준다: 1 … 4 [5] 6 … 12
  const pages = (() => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const out = [1];
    const s = Math.max(2, page - 1), e = Math.min(total - 1, page + 1);
    if (s > 2) out.push('…');
    for (let i = s; i <= e; i += 1) out.push(i);
    if (e < total - 1) out.push('…');
    out.push(total);
    return out;
  })();

  return (
    <>
      <form className="sp-search" onSubmit={(e) => { e.preventDefault(); setQuery(sword); setPage(1); }}>
        <select value={stype} onChange={(e) => setStype(e.target.value)} aria-label={T.searchBtn[lang]}>
          <option value="title">{T.searchTitle[lang]}</option>
          <option value="content">{T.searchContent[lang]}</option>
        </select>
        <input value={sword} onChange={(e) => setSword(e.target.value)} placeholder={T.searchPh[lang]} />
        <button type="submit">{T.searchBtn[lang]}</button>
      </form>

      <div className="sp-board">
        {rows !== null && all.length === 0 && <div className="sp-empty">{T.empty[lang]}</div>}
        {rows !== null && all.length > 0 && list.length === 0 && <div className="sp-empty">{T.noResult[lang]}</div>}
        {list.slice((page - 1) * PAGE, page * PAGE).map((n) => {
          const title = cleanTitle(pick(n, 'TITLE'));
          const inner = (
            <>
              <span className="tag">{tag[lang]}</span>
              <span className="title" title={title}>{title}</span>
              <span className="date">{fmtDate(n.CREATED_AT)}</span>
              <span className="arrow">→</span>
            </>
          );
          return (
            <a className="sp-brow" key={n.IDX} href={`?idx=${n.IDX}`}
               onClick={(e) => { e.preventDefault(); open(n); }}>{inner}</a>
          );
        })}
      </div>

      {total > 1 && (
        <>
          <div className="sp-pager">
            <button type="button" onClick={() => go(1)} disabled={page === 1}>«</button>
            <button type="button" onClick={() => go(page - 1)} disabled={page === 1}>‹</button>
            {pages.map((n, i) =>
              n === '…'
                ? <span className="gap" key={`g${i}`}>…</span>
                : <button type="button" key={n} className={n === page ? 'on' : ''} onClick={() => go(n)}>{n}</button>
            )}
            <button type="button" onClick={() => go(page + 1)} disabled={page === total}>›</button>
            <button type="button" onClick={() => go(total)} disabled={page === total}>»</button>
          </div>
          <div className="sp-count">
            {T.count[lang]((page - 1) * PAGE + 1, Math.min(page * PAGE, list.length), list.length)}
          </div>
        </>
      )}
    </>
  );
}

export default Board;
